import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  LinearProgress,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Grid,
} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import { Close, Add, HelpOutline } from '@mui/icons-material';
import { SportType, SUPPORTED_SPORTS, Event, LevelId } from '../types/event';
import { eventApi, tagApi } from '../utils/api';
import SportsIcon from './SportsIcon';
import { useNavigate } from 'react-router-dom';
import AddressAutocomplete from './AddressAutocomplete';
import { getLevelsForSport, getLevelDisplayName, Level } from '../utils/levelDefinitions';
import { compressImage } from '../utils/imageCompression';
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';

interface CreateEventModalProps {
  open: boolean;
  onClose: () => void;
  onEventCreated?: () => void;
  editMode?: boolean;
  existingEvent?: Event;
}

type CreateEventStep = 'details' | 'whenWhere' | 'requirements' | 'review';

interface Tag {
  id: string;
  name: string;
  color?: string;
  description?: string;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({
  open,
  onClose,
  onEventCreated,
  editMode = false,
  existingEvent
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<CreateEventStep>('details');
  const [formData, setFormData] = useState({
    title: '',
    sport: '' as SportType | '',
    description: '',
    address: '',
    formatted_address: '',
    latitude: null as number | null,
    longitude: null as number | null,
    date: '',
    time: '',
    capacity: 10,
    levelRequirement: '' as LevelId | '',
    joinType: 'free' as 'free' | 'request',
    cancellationDeadline: 24,
  });

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [eventImages, setEventImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [otherSportMode, setOtherSportMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [levelHelpAnchor, setLevelHelpAnchor] = useState<HTMLElement | null>(null);

  // Load available tags when modal opens
  useEffect(() => {
    if (open) {
      loadTags();
    }
  }, [open]);

  // Reload tags when sport changes
  useEffect(() => {
    if (open && formData.sport) {
      loadTags(formData.sport);
      // Clear selected tags when sport changes
      setSelectedTags([]);
    }
  }, [formData.sport, open]);

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && existingEvent && open) {
      const eventDate = new Date(existingEvent.starts_at);
      const dateStr = eventDate.toISOString().split('T')[0];
      const timeStr = eventDate.toTimeString().slice(0, 5);

      setFormData({
        title: existingEvent.title,
        sport: (() => {
          const activityType = existingEvent.activity_type;
          if (!activityType) return '';
          // Normalize to match SportType format (capitalized); keep custom sports as-is
          const normalized = SUPPORTED_SPORTS.find(s => s.toLowerCase() === activityType.toLowerCase());
          return (normalized as SportType) || (activityType as SportType);
        })(),
        description: existingEvent.description || '',
        address: existingEvent.address || '',
        formatted_address: existingEvent.formatted_address || '',
        latitude: existingEvent.latitude || null,
        longitude: existingEvent.longitude || null,
        date: dateStr,
        time: timeStr,
        capacity: existingEvent.capacity,
        levelRequirement: '' as LevelId | '',
        joinType: 'request',
        cancellationDeadline: existingEvent.cancellation_deadline_hours || 24,
      });

      const at = existingEvent.activity_type;
      setOtherSportMode(!!at && !SUPPORTED_SPORTS.some(s => s.toLowerCase() === at.toLowerCase()));

      // Set images if they exist
      const previews: string[] = [];
      if (existingEvent.images?.image_1) previews.push(existingEvent.images.image_1);
      if (existingEvent.images?.image_2) previews.push(existingEvent.images.image_2);
      if (existingEvent.images?.image_3) previews.push(existingEvent.images.image_3);
      setImagePreviews(previews);

      // Set tags
      if (existingEvent.tags) {
        setSelectedTags(existingEvent.tags.map(tag => tag.id));
      }

      // Skip welcome step in edit mode
      setStep('details');
    }
  }, [editMode, existingEvent, open]);

  const loadTags = async (sport?: string) => {
    try {
      setTagsLoading(true);
      const tags = await tagApi.getTags(sport);
      setAvailableTags(tags);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setTagsLoading(false);
    }
  };

  const getStepNumber = (): number => {
    const stepMap: Record<CreateEventStep, number> = {
      details: 0,
      whenWhere: 1,
      requirements: 2,
      review: 3,
    };
    return stepMap[step];
  };

  const getProgress = (): number => {
    return (getStepNumber() / 3) * 100;
  };

  const handleInputChange = (field: string, value: any) => {
    // Normalize sport value to match SportType format (capitalized)
    if (field === 'sport' && typeof value === 'string' && value) {
      const normalizedSport = SUPPORTED_SPORTS.find(s => s.toLowerCase() === value.toLowerCase());
      if (normalizedSport) {
        value = normalizedSport;
      }
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedTags(typeof value === 'string' ? value.split(',') : value);
  };

  const toggleTag = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(t => t !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      setError('Please enter a tag name');
      return;
    }
    if (!formData.sport) {
      setError('Please select a sport first');
      return;
    }

    try {
      setCreatingTag(true);
      setError(null);

      const newTag = await tagApi.createTag({
        name: newTagName.trim(),
        sport_type: formData.sport
      });

      setAvailableTags([...availableTags, newTag]);
      setSelectedTags([...selectedTags, newTag.id]);
      setNewTagName('');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to create tag';
      setError(errorMessage);
      console.error('Error creating tag:', err);
    } finally {
      setCreatingTag(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const remainingSlots = 3 - eventImages.length;
      const filesToAdd = files.slice(0, remainingSlots);

      setEventImages([...eventImages, ...filesToAdd]);
      setError(null);

      // Use compressImage which handles HEIC conversion and compression
      const compressionPromises = filesToAdd.map(async (file) => {
        try {
          // compressImage handles HEIC conversion, compression, and returns base64 data URL
          const compressedImage = await compressImage(file, {
            maxSizeMB: 4,
            maxWidthOrHeight: 1920,
            quality: 0.85
          });
          return compressedImage;
        } catch (error: any) {
          console.error('Error processing image:', error);
          // If it's a HEIC conversion error, show a helpful message
          if (error.message && error.message.includes('HEIC')) {
            throw new Error(`Failed to convert HEIC image: ${error.message}`);
          }
          throw new Error(`Failed to process image: ${error.message || 'Unknown error'}`);
        }
      });

      try {
        const compressedImages = await Promise.all(compressionPromises);
        setImagePreviews((prev) => [...prev, ...compressedImages]);
      } catch (error: any) {
        console.error('Error processing images:', error);
        setError(error.message || 'Failed to process some images. Please try converting HEIC images to JPEG first.');
        // Remove the files that failed
        setEventImages(eventImages);
      }
    }
  };

  const removeImage = (index: number) => {
    setEventImages(eventImages.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    setError('');

    // In edit mode, validate and submit from any step


    // Normal flow for creating new events
    if (step === 'details') {
      if (!formData.title.trim()) {
        setError('Event title is required');
        return;
      }
      if (!formData.sport) {
        setError('Please select a sport');
        return;
      }
      // Require at least one image for new events (not in edit mode)
      if (!editMode && imagePreviews.length === 0) {
        setError('Please upload at least one image for your event');
        return;
      }
      setStep('whenWhere');
    } else if (step === 'whenWhere') {
      if (!formData.date || !formData.time) {
        setError('Please select both date and time');
        return;
      }
      setStep('requirements');
    } else if (step === 'requirements') {
      setStep('review');
    } else if (step === 'review') {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step === 'whenWhere') {
      setStep('details');
    } else if (step === 'requirements') {
      setStep('whenWhere');
    } else if (step === 'review') {
      setStep('requirements');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Require at least one image for new events (not in edit mode)
      if (!editMode && imagePreviews.length === 0) {
        setError('Please upload at least one image for your event');
        setLoading(false);
        return;
      }

      // Combine date and time
      const eventDateTime = new Date(`${formData.date}T${formData.time}`);

      // Prepare event data
      const eventData: any = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        starts_at: eventDateTime.toISOString(),
        capacity: formData.capacity,
        activity_type: formData.sport.toLowerCase().replace('/', '-'),
        address: formData.address.trim() || undefined,
        formatted_address: formData.formatted_address.trim() || undefined,
        latitude: formData.latitude || undefined,
        longitude: formData.longitude || undefined,
        tag_ids: selectedTags,
        cancellation_deadline_hours: formData.cancellationDeadline,
        level_requirement: formData.levelRequirement || undefined,
      };

      // Add images for update (they're already base64 in imagePreviews for edit mode)
      if (editMode) {
        // Validate image previews before sending
        const validatedImages: (string | null)[] = [];
        for (let i = 0; i < 3; i++) {
          const preview = imagePreviews[i];
          if (preview) {
            // Ensure it's a valid JPEG/PNG/GIF/WebP data URL
            if (preview.startsWith('data:image/')) {
              const mimeType = preview.split(',')[0];
              if (mimeType.includes('heic') || mimeType.includes('heif')) {
                setError(`Image ${i + 1} is still in HEIC format. Please try uploading again.`);
                setLoading(false);
                return;
              }
              if (!mimeType.match(/data:image\/(jpeg|jpg|png|gif|webp)/i)) {
                setError(`Image ${i + 1} has unsupported format. Only JPEG, PNG, GIF, and WebP are allowed.`);
                setLoading(false);
                return;
              }
            }
            validatedImages[i] = preview;
          } else {
            validatedImages[i] = null;
          }
        }
        eventData.image_1 = validatedImages[0] || null;
        eventData.image_2 = validatedImages[1] || null;
        eventData.image_3 = validatedImages[2] || null;
      } else {
        eventData.auto_accept = formData.joinType === 'free';
      }

      if (editMode && existingEvent) {
        // Update existing event
        await eventApi.updateEvent(existingEvent.id, eventData);
        handleClose();

        if (onEventCreated) {
          onEventCreated();
        }
      } else {
        // Create new event
        const createdEvent = await eventApi.createEvent(eventData);
        
        // Upload images if any
        if (imagePreviews.length > 0) {
          try {
            await eventApi.uploadEventImages(createdEvent.id, {
              image_1: imagePreviews[0] || null,
              image_2: imagePreviews[1] || null,
              image_3: imagePreviews[2] || null,
            });
          } catch (imageError) {
            console.error('Error uploading images:', imageError);
            // Don't fail the whole operation if images fail
            setError('Event created but some images failed to upload');
          }
        }
        
        handleClose();
        navigate(`/event/${createdEvent.id}`);

        if (onEventCreated) {
          onEventCreated();
        }
      }

    } catch (error: any) {
      console.error(editMode ? 'Error updating event:' : 'Error creating event:', error);
      
      // Extract detailed error message
      let errorMessage = `Failed to ${editMode ? 'update' : 'create'} event. Please try again.`;
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map((err: any) => err.msg || err).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Log full error for debugging
      console.error('Full error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        errorMessage
      });
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setStep('details');
      setFormData({
        title: '',
        sport: '',
        description: '',
        address: '',
        formatted_address: '',
        latitude: null,
        longitude: null,
        date: '',
        time: '',
        capacity: 10,
        levelRequirement: '' as LevelId | '',
        joinType: 'free',
        cancellationDeadline: 24,
      });
      setSelectedTags([]);
      setEventImages([]);
      setImagePreviews([]);
      setOtherSportMode(false);
      setError(null);
      onClose();
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 'details':
        return (
          <Box>
            <Typography variant="overline" color="primary" fontWeight="bold">
              STEP 1
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
              Event details
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Event Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                fullWidth
                required
                placeholder="e.g., Morning Tennis Match"
              />

              <TextField
                select
                label="Sport"
                value={otherSportMode ? '__other__' : formData.sport}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__other__') {
                    setOtherSportMode(true);
                    handleInputChange('sport', '');
                  } else {
                    setOtherSportMode(false);
                    handleInputChange('sport', v);
                  }
                }}
                fullWidth
                required
                SelectProps={{
                  renderValue: (value: unknown) => {
                    if (!value || typeof value !== 'string') {
                      return <span style={{ color: '#999' }}>Select a sport</span>;
                    }
                    if (value === '__other__') {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span style={{ fontSize: 20 }}>➕</span>
                          <span>Other</span>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SportsIcon 
                          sport={value as SportType} 
                          size={20} 
                          style={{ filter: 'brightness(0) saturate(100%)' }}
                        />
                        <span>{value}</span>
                      </Box>
                    );
                  }
                }}
              >
                {SUPPORTED_SPORTS.map((sport) => (
                  <MenuItem key={sport} value={sport}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SportsIcon 
                        sport={sport} 
                        size={20} 
                        style={{ filter: 'brightness(0) saturate(100%)' }}
                      />
                      <span>{sport}</span>
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem value="__other__">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: 20 }}>➕</span>
                    <span>Other</span>
                  </Box>
                </MenuItem>
              </TextField>

              {otherSportMode && (
                <TextField
                  label="Your sport"
                  value={formData.sport}
                  onChange={(e) => handleInputChange('sport', e.target.value)}
                  fullWidth
                  required
                  placeholder="e.g. Squash, Volleyball, Rock climbing"
                  inputProps={{ maxLength: 40 }}
                />
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Upload images for your event <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              {!editMode && imagePreviews.length === 0 && (
                <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                  At least one image is required
                </Typography>
              )}

              {/* Upload box - only show if less than 3 images */}
              {imagePreviews.length < 3 && (
                <Box
                  component="label"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed #e0e0e0',
                    borderRadius: 2,
                    py: 4,
                    px: 3,
                    cursor: 'pointer',
                    mb: imagePreviews.length > 0 ? 2 : 0,
                    minHeight: 120,
                    '&:hover': {
                      borderColor: '#533B7D',
                      backgroundColor: '#f5f0ff',
                    },
                  }}
                >
                  <Add sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Click to upload (max 3 images)
                  </Typography>
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                  />
                </Box>
              )}

              {imagePreviews.length > 0 && (
                <Grid container spacing={2}>
                  {imagePreviews.map((preview, index) => (
                    <Grid item xs={4} key={index}>
                      <Box sx={{ position: 'relative', paddingTop: '75%', borderRadius: 2, overflow: 'hidden' }}>
                        <Box
                          component="img"
                          src={preview}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <IconButton
                          onClick={() => removeImage(index)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                            },
                          }}
                          size="small"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
              placeholder="Add any additional details about your event..."
            />
          </Box>
        );

      case 'whenWhere':
        return (
          <Box>
            <Typography variant="overline" color="primary" fontWeight="bold">
              STEP 2
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
              When & where
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <AddressAutocomplete
                value={formData.address}
                onChange={(address) => handleInputChange('address', address)}
                onSelect={(address, lat, lng) => {
                  setFormData(prev => ({
                    ...prev,
                    address: address,
                    formatted_address: address,
                    latitude: lat ?? null,
                    longitude: lng ?? null,
                  }));
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Event Date"
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0],
                  autoComplete: 'off',
                }}
              />
              <TextField
                label="Event Time"
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  autoComplete: 'off',
                }}
              />
            </Box>
          </Box>
        );

      case 'requirements':
        return (
          <Box>
            <Typography variant="overline" color="primary" fontWeight="bold">
              STEP 3
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
              Requirements
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="Number of Players"
              type="number"
              value={formData.capacity}
              onChange={(e) => handleInputChange('capacity', parseInt(e.target.value) || 1)}
              fullWidth
              inputProps={{ min: 1, max: 100 }}
              sx={{ mb: 3 }}
            />

            {formData.sport && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Level Requirements
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => setLevelHelpAnchor(e.currentTarget)}
                    sx={{ color: 'text.secondary', p: 0.5 }}
                  >
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  select
                  value={formData.levelRequirement || ''}
                  onChange={(e) => handleInputChange('levelRequirement', e.target.value)}
                  fullWidth
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value: unknown) => {
                      if (!value || !formData.sport) {
                        return <span style={{ color: '#999' }}>Select a level</span>;
                      }
                      // Normalize sport for display
                      const normalizedSport = SUPPORTED_SPORTS.find(s => s.toLowerCase() === formData.sport.toLowerCase());
                      return getLevelDisplayName(value as LevelId, normalizedSport as SportType);
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Any Level</em>
                  </MenuItem>
                  {(() => {
                    // Normalize sport value to match SportType format
                    const sportValue = formData.sport;
                    const normalizedSport = sportValue ? SUPPORTED_SPORTS.find(s => s.toLowerCase() === sportValue.toLowerCase()) : null;
                    const isInSupported = normalizedSport ? SUPPORTED_SPORTS.includes(normalizedSport as SportType) : false;
                    const levels = normalizedSport ? getLevelsForSport(normalizedSport as SportType) : [];
                    return normalizedSport && isInSupported ? 
                      levels.map((level) => (
                        <MenuItem key={level.id} value={level.id}>
                          {level.range} - {level.genericName}
                        </MenuItem>
                      )) : [];
                  })()}
                </TextField>
                <Popover
                  open={Boolean(levelHelpAnchor)}
                  anchorEl={levelHelpAnchor}
                  onClose={() => setLevelHelpAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Box sx={{ p: 2, maxWidth: 400 }}>
                    <Typography variant="h6" gutterBottom>
                      Level Guide - {formData.sport}
                    </Typography>
                    {formData.sport ? (
                      (() => {
                        const normalizedSport = SUPPORTED_SPORTS.find(s => s.toLowerCase() === formData.sport.toLowerCase());
                        const levels = normalizedSport ? getLevelsForSport(normalizedSport as SportType) : [];
                        if (levels.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No level information available for {formData.sport}
                            </Typography>
                          );
                        }
                        return (
                          <List dense>
                            {levels.map((level, index) => (
                              <React.Fragment key={level.id}>
                                <ListItem>
                                  <ListItemText
                                    primary={
                                      <Box>
                                        <Typography variant="subtitle2" component="span" fontWeight="bold">
                                          {level.range}
                                        </Typography>
                                        <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                          ({level.genericName})
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <Box sx={{ mt: 0.5 }}>
                                        <Typography variant="body2">{level.description}</Typography>
                                        {level.type && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {level.type}
                                          </Typography>
                                        )}
                                        {level.description2 && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {level.description2}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItem>
                                {index < levels.length - 1 && <Divider />}
                              </React.Fragment>
                            ))}
                          </List>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Please select a sport first
                      </Typography>
                    )}
                  </Box>
                </Popover>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tags (preferences)
              </Typography>
              {tagsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {availableTags.map((tag) => {
                      const isSelected = selectedTags.includes(tag.id);
                      return (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          onClick={() => toggleTag(tag.id)}
                          onDelete={isSelected ? () => toggleTag(tag.id) : undefined}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{
                            backgroundColor: isSelected && tag.color ? tag.color : undefined,
                            '&:hover': {
                              backgroundColor: isSelected && tag.color ? tag.color : undefined,
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                  {formData.sport && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Add custom tag..."
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !creatingTag) {
                            handleCreateTag();
                          }
                        }}
                        sx={{ flex: 1 }}
                        disabled={creatingTag}
                      />
                      <Button
                        variant="outlined"
                        onClick={handleCreateTag}
                        disabled={creatingTag || !newTagName.trim()}
                        sx={{ textTransform: 'none' }}
                      >
                        {creatingTag ? 'Adding...' : 'Add Tag'}
                      </Button>
                    </Box>
                  )}
                  {availableTags.length === 0 && formData.sport && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No tags available yet. Create your own custom tags above!
                    </Typography>
                  )}
                </>
              )}
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Join type
              </Typography>
              <RadioGroup
                value={formData.joinType}
                onChange={(e) => handleInputChange('joinType', e.target.value)}
              >
                <FormControlLabel
                  value="request"
                  control={<Radio />}
                  label="By request only"
                />
                <FormControlLabel
                  value="free"
                  control={<Radio />}
                  label="Free to join"
                />
              </RadioGroup>
            </Box>

            <Box sx={{ mt: 3 }}>
              <TextField
                label="Cancellation Deadline (hours before start)"
                type="number"
                value={formData.cancellationDeadline}
                onChange={(e) => handleInputChange('cancellationDeadline', parseInt(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 0 }}
                helperText="Participants can cancel their spot until this many hours before the event starts."
              />
            </Box>
          </Box>
        );

      case 'review':
        return (
          <Box>
            <Typography variant="overline" color="primary" fontWeight="bold">
              STEP 4
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
              Review and submit
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Event Images */}
            {imagePreviews.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={1}>
                  {imagePreviews.map((preview, index) => (
                    <Grid item xs={index === 0 ? 6 : 3} key={index}>
                      <Box
                        component="img"
                        src={preview}
                        sx={{
                          width: '100%',
                          height: index === 0 ? 200 : 100,
                          objectFit: 'cover',
                          borderRadius: 2,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Event Details */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>Event Details</Typography>
              <Box sx={{ borderBottom: '1px solid #e0e0e0', pb: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">TITLE</Typography>
                    <Typography variant="body1">{formData.title}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">SPORT</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SportsIcon 
                        sport={formData.sport as SportType} 
                        size={20} 
                        style={{ filter: 'brightness(0) saturate(100%)' }}
                      />
                      <Typography variant="body1">{formData.sport}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              {formData.description && (
                <Box>
                  <Typography variant="caption" color="text.secondary">DESCRIPTION</Typography>
                  <Typography variant="body2">{formData.description}</Typography>
                </Box>
              )}
            </Box>

            {/* When & Where */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>When & Where</Typography>
              <Box sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    {formData.address && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">LOCATION</Typography>
                        <Typography variant="body1">{formData.address}</Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">DATE</Typography>
                      <Typography variant="body1">
                        {new Date(formData.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">TIME</Typography>
                      <Typography variant="body1">{formData.time}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Requirements */}
            <Box>
              <Typography variant="h6" gutterBottom>Player Requirements</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">NUMBER OF PLAYERS</Typography>
                  <Typography variant="body1">{formData.capacity} Players</Typography>
                </Grid>
                {formData.levelRequirement && formData.sport && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">LEVEL REQUIREMENTS</Typography>
                    <Typography variant="body1">
                      {getLevelDisplayName(formData.levelRequirement, formData.sport)}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">CANCELLATION DEADLINE</Typography>
                  <Typography variant="body1">{formData.cancellationDeadline} hours before start</Typography>
                </Grid>
              </Grid>
              {selectedTags.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">TAGS</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {selectedTags.map((tagId) => {
                      const tag = availableTags.find(t => t.id === tagId);
                      return tag ? (
                        <Chip
                          key={tagId}
                          label={tag.name}
                          size="small"
                          sx={{
                            backgroundColor: tag.color || undefined,
                            color: tag.color ? '#fff' : undefined,
                          }}
                        />
                      ) : null;
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          minHeight: 'auto',
          overscrollBehavior: 'contain',
        }
      }}
    >
      <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 2 }}>
        <Typography variant="h4" component="h2" fontWeight="bold">
          {editMode ? 'Edit Event' : 'Create Event'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={loading}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={getProgress()}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#533B7D',
            },
          }}
        />
      </Box>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {renderStepContent()}

          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            {step !== 'details' && (
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                sx={{
                  py: 1.5,
                  px: 4,
                  textTransform: 'none',
                  borderRadius: 2,
                }}
              >
                Back
              </Button>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              sx={{
                py: 1.5,
                backgroundColor: '#533B7D',
                '&:hover': {
                  backgroundColor: '#4a337a',
                },
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : step === 'review' ? (
                editMode ? 'Update Event' : 'Create Event'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </ResponsiveDialog>
  );
};

export default CreateEventModal;
